import memoizee from 'memoizee';

export const memoize = <A extends Array<unknown>, R>(func: (...args: A) => R) =>
    memoizee(func, {
        normalizer: (args) => JSON.stringify(args),
        primitive: true,
    });

export const primitiveMemoize = <A extends Array<unknown>, R>(func: (...args: A) => R) =>
    memoizee(func, {
        primitive: true,
    });
/**
 * Creates a WeakMap-based selector for caching computed values, ideal for react-query `select` option.
 * The value is calculated once for each unique state instance, requiring referentially stable state objects.
 * Uses WeakMap to avoid blocking garbage collection of state objects, making it ideal when the cache
 * is maintained outside of this function (e.g., query cache).
 *
 * Usage: Create the selector outside of the query hook, ideally have at most one cache per query type.
 * Leverage existing cached selectors when possible. Intended for high-performance scenarios where cached `select`
 * operations reduce query computation cost.
 *
 * @param selector - the referentially stable selector function
 * @returns memoized selector function
 */
export const createWeakMapSelector = <S extends object, R>(selector: (state: S) => R) => {
    const weakMap = new WeakMap<S, R>();
    return (state: S): R => {
        if (!weakMap.has(state)) {
            weakMap.set(state, selector(state));
        }
        return weakMap.get(state) as R;
    };
};

/**
 * Creates a memory-safe memoized selector that accepts both state and props parameters.
 *
 * Ideal for React Query `select` option or any scenario where you need high-performance
 * caching without memory leaks. The cache automatically cleans itself when results
 * are no longer referenced elsewhere in your application.
 *
 * **Usage Guidelines:**
 * - Create the selector outside of components/hooks to maintain cache across renders
 * - State objects must be referentially stable (same object reference for same state)
 * - Props must be referentially stable for object values (uses === comparison)
 * - Result type must be objects (WeakRef cannot hold primitive values)
 *
 * @example
 * ```typescript
 * // Create once, reuse everywhere
 * const selectUserProfile = createWeakMapSelectorWithProps(
 *   (state: UserState, userId: string) => {
 *     return state.users.find(u => u.id === userId)?.profile || null;
 *   }
 * );
 *
 * // In React Query
 * const { data } = useRepositoryQuery(
    'reservations',
    'getAll',
    {
        groupIds: [groupId],
    },
    {
        select: (data) => selectUserProfile(data, currentUserId)
    }
 * });
 *
 * // Or anywhere else
 * const profile = selectUserProfile(state, "user123");
 * ```
 *
 * @param selector - Function that computes a result from state and props
 * @returns Memoized selector with automatic memory management
 */
export const createWeakMapSelectorWithProps = <S extends object, P, R extends object>(
    selector: (state: S, props: P) => R
) => {
    const weakMap = new WeakMap<S, { resultMap: Map<P, WeakRef<R>>; finalizationRegistry: FinalizationRegistry<P> }>();

    return (state: S, props: P): R => {
        let propsMap = weakMap.get(state);
        if (!propsMap) {
            const storedData = {
                resultMap: new Map<P, WeakRef<R>>(),
                finalizationRegistry: new FinalizationRegistry<P>((p) => storedData.resultMap.delete(p)),
            };

            weakMap.set(state, storedData);
            propsMap = storedData;
        }

        const weakRef = propsMap.resultMap.get(props);
        if (weakRef) {
            const value = weakRef.deref();
            if (value) {
                return value;
            }
        }

        // Compute new result and cache it
        const computedValue = selector(state, props);
        const toFinalize = new WeakRef(computedValue);
        // Save the computed value in the WeakMap and register it for finalization after it gets garbage collected
        propsMap.resultMap.set(props, toFinalize);
        propsMap.finalizationRegistry.register(computedValue, props);

        return computedValue;
    };
};
